import type { FieldErrors, UseFormRegister } from 'react-hook-form';
import {
  FieldError,
  FieldLabel,
  SectionCard,
  campInputClassFor,
} from '@/features/campaigns/components/FormPrimitives';
import type { AddClientFormValues } from '../AddClientPage.types';
import { inputValidation } from '@/utils/function';

const sanitizeEmoji = (value: string) =>
  inputValidation(value, { emoji: true, specialCharacters: false });

type ClientAddressSectionProps = {
  register: UseFormRegister<AddClientFormValues>;
  errors: FieldErrors<AddClientFormValues>;
  variant?: 'card' | 'plain';
};

export function ClientAddressSection({
  register,
  errors,
  variant = 'card',
}: ClientAddressSectionProps) {
  const inputClass = campInputClassFor(variant);

  return (
    <SectionCard
      step={3}
      title="Address"
      description="Where is the client based?"
      variant={variant}
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
        <div className="sm:col-span-3">
          <FieldLabel required>Address of Client</FieldLabel>
          <input
            type="text"
            placeholder="Street, locality, city"
            className={inputClass}
            aria-invalid={Boolean(errors.address)}
            {...register('address', {
              required: 'Address is required',
              setValueAs: (value) => sanitizeEmoji(String(value ?? '')),
              onChange: (e) => {
                e.target.value = sanitizeEmoji(e.target.value);
              },
            })}
          />
          <FieldError message={errors.address?.message} />
        </div>
        <div>
          <FieldLabel required>PIN</FieldLabel>
          <input
            type="text"
            inputMode="numeric"
            autoComplete="postal-code"
            placeholder="6-digit PIN"
            maxLength={6}
            className={inputClass}
            aria-invalid={Boolean(errors.pin)}
            {...register('pin', {
              required: 'PIN is required',
              pattern: {
                value: /^[1-9][0-9]{5}$/,
                message: 'Enter a valid 6-digit PIN code',
              },
              minLength: { value: 6, message: 'PIN must be 6 digits' },
              maxLength: { value: 6, message: 'PIN must be 6 digits' },
              setValueAs: (value) =>
                String(value ?? '')
                  .replace(/\D/g, '')
                  .slice(0, 6),
              onChange: (e) => {
                e.target.value = String(e.target.value ?? '')
                  .replace(/\D/g, '')
                  .slice(0, 6);
              },
            })}
          />
          <FieldError message={errors.pin?.message} />
        </div>
      </div>
    </SectionCard>
  );
}
